import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Panel } from '@/ui';
import { Icon, type IconName } from '@/icons';
import { useSession } from '@/hooks';
import { useFeedbackPeek, useFeedbackStats } from '@/hooks/useFeedback';
import {
  FEEDBACK_CATEGORY_LABELS,
  type FeedbackCategory,
  type FeedbackStats,
} from '@/core/models/feedback';
import { CATEGORY_ICONS } from '../feedbackMeta';
import type { FeedbackQuery } from '@/core/services/feedbackService';

const Card = styled(Panel)`
  padding: 18px 16px;
  background-image:
    linear-gradient(125deg, ${({ theme }) => theme.color.brandFrom}0d, transparent 70%),
    ${({ theme }) => theme.surfaceGradients.panel};
`;
const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  > span {
    color: ${({ theme }) => theme.color.brandFrom};
  }
  h2 {
    flex: 1;
    margin: 0;
    font-size: 15px;
  }
  a {
    flex-shrink: 0;
    font-size: 11px;
  }
`;
const Text = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.55;
  margin: 0 0 14px;
`;
const More = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.color.brandFrom};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
`;
const List = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;
const Row = styled.li`
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  padding: 10px 0;
  &:last-child {
    padding-bottom: 0;
  }
  a {
    display: flex;
    align-items: center;
    gap: 12px;
    color: inherit;
    text-decoration: none;
  }
  a:hover strong {
    color: ${({ theme }) => theme.color.brandFrom};
  }
  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
  strong {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 1.5;
  }
  small {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;
const Rank = styled.span`
  color: ${({ theme }) => theme.color.brandFrom};
  font-size: 22px;
  font-weight: 700;
`;
const ItemText = styled.span`
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
`;
const Category = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 5px;
  border-radius: 5px;
  background: ${({ theme }) => theme.color.brandFrom}12;
`;
const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  margin-bottom: 10px;
  button {
    flex: 1;
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    color: ${({ theme }) => theme.color.textMuted};
    padding: 8px 3px;
    cursor: pointer;
    font: inherit;
    font-size: 11px;
  }
  button[aria-pressed='true'] {
    color: ${({ theme }) => theme.color.textPrimary};
    border-bottom-color: ${({ theme }) => theme.color.brandFrom};
  }
  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;
const Metric = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  > span:last-child {
    margin-left: auto;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:last-child {
    border-bottom: 0;
  }
`;
const Dot = styled.span<{ $index: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme, $index }) =>
    [
      theme.color.brandFrom,
      theme.color.danger,
      theme.color.review,
      theme.color.success,
      theme.color.textMuted,
    ][$index % 5]};
`;
const Boundary = styled.div`
  display: flex;
  gap: 10px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  padding-top: 14px;
  margin-top: 16px;
  > span {
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.brandFrom};
  }
  p {
    margin: 0;
  }
`;

type Kind = 'trending' | 'mine' | 'recent' | 'planned';
const OPTIONS: Record<
  Kind,
  { title: string; icon: IconName; description: string; query: FeedbackQuery; href: string }
> = {
  trending: {
    title: 'Trending feedback',
    icon: 'flame',
    description: 'What the community is talking about.',
    query: { sort: 'top', limit: 3 },
    href: '/feedback?sort=top',
  },
  mine: {
    title: 'My feedback',
    icon: 'user',
    description: 'Ideas and reports you’ve shared.',
    query: { mine: true, sort: 'new', limit: 3 },
    href: '/feedback?filter=mine',
  },
  recent: {
    title: 'Recently updated',
    icon: 'clock',
    description: 'The latest changes across the board.',
    query: { sort: 'updated', limit: 3 },
    href: '/feedback?sort=updated',
  },
  planned: {
    title: 'Planned next',
    icon: 'package',
    description: 'Ideas on the roadmap.',
    query: { status: 'planned', sort: 'top', limit: 3 },
    href: '/feedback?status=planned',
  },
};
function FeedbackListPanel({ kind }: { kind: Kind }) {
  const option = OPTIONS[kind];
  const { items, loading, error, reload } = useFeedbackPeek(option.query);
  return (
    <Card data-testid={`feedback-${kind}-panel`}>
      <Heading>
        <Icon name={option.icon} size={23} />
        <h2>{option.title}</h2>
        <More to={option.href}>
          View all <Icon name="arrow-right" size={12} />
        </More>
      </Heading>
      <Text>{option.description}</Text>
      {loading ? (
        <Text role="status">Loading…</Text>
      ) : error ? (
        <>
          <Text role="alert">Could not load {option.title.toLowerCase()}.</Text>
          <Button variant="ghost" onClick={reload}>
            Try again
          </Button>
        </>
      ) : items.length === 0 ? (
        <Text>No feedback here yet.</Text>
      ) : (
        <List>
          {items.map((item, index) => (
            <Row key={item.id}>
              <Link to={`/feedback/${item.id}`}>
                {kind === 'trending' && <Rank>{index + 1}</Rank>}
                <ItemText>
                  <strong>{item.title}</strong>
                  <small>
                    <Category>
                      <Icon name={CATEGORY_ICONS[item.category]} size={11} />
                      {FEEDBACK_CATEGORY_LABELS[item.category]}
                    </Category>
                  </small>
                  <small>
                    {item.upvotes} {item.upvotes === 1 ? 'vote' : 'votes'}
                  </small>
                </ItemText>
              </Link>
            </Row>
          ))}
        </List>
      )}
    </Card>
  );
}
export function FeedbackOptionalPanel({ kind }: { kind: Kind }) {
  const { isAuthenticated } = useSession();
  if (kind === 'mine' && !isAuthenticated)
    return (
      <Card>
        <Heading>
          <Icon name="user" size={23} />
          <h2>My feedback</h2>
        </Heading>
        <Text>Sign in to see the ideas and reports you’ve shared.</Text>
        <More to="/signin?next=/feedback">
          Sign in <Icon name="arrow-right" size={12} />
        </More>
      </Card>
    );
  return <FeedbackListPanel kind={kind} />;
}

const CATEGORY_ORDER: FeedbackCategory[] = [
  'feature',
  'bug',
  'ux',
  'performance',
  'other',
  'accessibility',
  'moderation',
];
const STATUS_METRICS: { key: keyof FeedbackStats; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'underReview', label: 'Under review' },
  { key: 'planned', label: 'Planned' },
  { key: 'inProgress', label: 'In progress' },
  { key: 'testing', label: 'Testing' },
  { key: 'completed', label: 'Completed' },
  { key: 'declined', label: 'Declined' },
];
export function FeedbackInsightsPanel() {
  const [tab, setTab] = useState('Top categories');
  const { stats, loading, error, reload } = useFeedbackStats();
  const categories = CATEGORY_ORDER.filter(
    (c) =>
      ['feature', 'bug', 'ux', 'performance', 'other'].includes(c) ||
      (stats?.categoryCounts?.[c] ?? 0) > 0,
  );
  const ordered = [...categories].sort(
    (a, b) => (stats?.categoryCounts?.[b] ?? 0) - (stats?.categoryCounts?.[a] ?? 0),
  );
  return (
    <Card data-testid="feedback-insights-panel">
      <Heading>
        <Icon name="bar-chart-3" size={23} />
        <h2>Feedback insights</h2>
        <More to="/feedback/roadmap">
          View all <Icon name="arrow-right" size={12} />
        </More>
      </Heading>
      <Text>A quick look at what your community is sharing.</Text>
      <Tabs role="group" aria-label="Feedback insights view">
        {['Top categories', 'Recent activity', 'Status mix'].map((label) => (
          <button key={label} aria-pressed={tab === label} onClick={() => setTab(label)}>
            {label}
          </button>
        ))}
      </Tabs>
      {loading ? (
        <Text role="status">Loading insights…</Text>
      ) : error || !stats ? (
        <>
          <Text role="alert">Could not load feedback insights.</Text>
          <Button variant="ghost" onClick={reload}>
            Try again
          </Button>
        </>
      ) : (
        <div>
          {tab === 'Top categories' &&
            (stats.categoryCounts ? (
              ordered.map((category) => (
                <Metric key={category}>
                  <Dot $index={CATEGORY_ORDER.indexOf(category)} />
                  <span>
                    {category === 'feature'
                      ? 'Feature requests'
                      : category === 'bug'
                        ? 'Bug reports'
                        : FEEDBACK_CATEGORY_LABELS[category]}
                  </span>
                  <span>{stats.categoryCounts?.[category] ?? 0}</span>
                </Metric>
              ))
            ) : (
              <Text>Category insights aren’t available from this server yet.</Text>
            ))}
          {tab === 'Recent activity' && (
            <>
              <Text>Across the last 7 days</Text>
              <Metric>
                <Icon name="plus" size={15} />
                <span>New feedback</span>
                <span>{stats.newThisWeek}</span>
              </Metric>
              <Metric>
                <Icon name="clock" size={15} />
                <span>Items updated</span>
                <span>{stats.updatedThisWeek ?? 'No data'}</span>
              </Metric>
              <More to="/feedback?sort=updated">
                Explore recent updates <Icon name="arrow-right" size={12} />
              </More>
            </>
          )}
          {tab === 'Status mix' &&
            STATUS_METRICS.map((metric, index) => (
              <Metric key={metric.key}>
                <Dot $index={index} />
                <span>{metric.label}</span>
                <span>{String(stats[metric.key] ?? 0)}</span>
              </Metric>
            ))}
        </div>
      )}
    </Card>
  );
}

export function FeedbackGivePanel({ onOpen }: { onOpen: () => void }) {
  return (
    <Card data-testid="give-feedback-panel">
      <Heading>
        <Icon name="comment" size={23} />
        <h2>Give feedback</h2>
      </Heading>
      <Text>
        Post a bug, an idea, or something that felt wrong. Upvotes surface what matters most, and
        every item carries a public roadmap you can follow.
      </Text>
      <Button variant="ghost" onClick={onOpen}>
        Share an idea <Icon name="arrow-right" size={14} />
      </Button>
      <Boundary>
        <Icon name="shield" size={22} />
        <Text>
          This board is for product feedback. Direct threats, terrorism, scams, fraud or coordinated
          abuse belong in Report on the post, and those go to the Council.{' '}
          <More to="/help/reporting">
            Read how <Icon name="arrow-right" size={12} />
          </More>
        </Text>
      </Boundary>
    </Card>
  );
}
